/** tm/legal/LegalMemoLinks.tsx — 내 메모(자동 저장) + 첨부 파일(카드 전체에 끌어다 놓기) + 업체 연결(검색 선택 · × 해제) */
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { C, T, R, IMEArea } from '../components/ui';
import { saveMemo, linkCompany, unlinkCompany } from './legalApi';
import { errMsg, labelStyle, MUTED } from './legalTheme';
import CompanyPicker, { type PickCompany } from './CompanyPicker';
import { DETAIL_MAX } from './LegalDetailParts';
import LegalMemoFiles, { useMemoFiles } from './LegalMemoFiles';
import type { LinkedCompany, LegalMemoFile } from './legalTypes';

interface Props {
  itemId: number; memo: string; linked: LinkedCompany[]; companies: PickCompany[] | null; files?: LegalMemoFile[] | null; // companies null = 업체 연결 숨김
  onToast: (m: string, k?: 'ok' | 'error') => void; onLinked: (list: LinkedCompany[]) => void;
}
type SaveState = '' | 'saving' | 'saved' | 'error';

export default function LegalMemoLinks({ itemId, memo, linked, companies, files, onToast, onLinked }: Props) {
  const [text, setText] = useState(memo);
  const fileSt = useMemoFiles(itemId, files, onToast);
  // 끌어다 놓기 — 자식 요소를 지날 때 깜빡이지 않도록 진입 횟수로 판단, 파일일 때만 반응
  const [drag, setDrag] = useState(false);
  const depth = useRef(0);
  const isFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragEnter = (e: DragEvent) => { if (!isFiles(e)) return; e.preventDefault(); depth.current += 1; setDrag(true); };
  const onDragOver = (e: DragEvent) => { if (!isFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const onDragLeave = (e: DragEvent) => { if (!isFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (!depth.current) setDrag(false); };
  const onDrop = (e: DragEvent) => {
    if (!isFiles(e)) return;
    e.preventDefault(); depth.current = 0; setDrag(false);
    if (e.dataTransfer.files?.length) fileSt.addFiles(e.dataTransfer.files);
  };
  // 창 밖에서 놓거나 Esc 로 취소하면 dragleave 가 모자랄 수 있다 — 끝나면 강조를 끈다
  useEffect(() => {
    if (!drag) return undefined;
    const reset = () => { depth.current = 0; setDrag(false); };
    window.addEventListener('dragend', reset); window.addEventListener('drop', reset);
    return () => { window.removeEventListener('dragend', reset); window.removeEventListener('drop', reset); };
  }, [drag]);
  const [st, setSt] = useState<SaveState>('');
  const [busy, setBusy] = useState(false);
  const last = useRef(memo);
  const pending = useRef({ id: itemId, text });
  useEffect(() => { pending.current = { id: itemId, text }; });
  useEffect(() => { setText(memo); last.current = memo; setSt(''); }, [itemId, memo]);
  // 다른 글로 넘어가거나 닫힐 때 저장 안 된 메모는 바로 보낸다
  useEffect(() => () => {
    const p = pending.current;
    if (p.id === itemId && p.text !== last.current) saveMemo(p.id, p.text).catch(() => undefined);
  }, [itemId]);
  useEffect(() => {
    if (text === last.current) return undefined;
    setSt('saving');
    const t = setTimeout(async () => {
      try { await saveMemo(itemId, text); last.current = text; setSt('saved'); }
      catch { setSt('error'); }
    }, 800);
    return () => clearTimeout(t);
  }, [text, itemId]);

  const add = async (c: PickCompany) => {
    setBusy(true);
    try {
      const d = await linkCompany(itemId, c.id);
      onLinked(d?.companies || [{ company_id: c.id, name: c.name, note: null, linked_at: new Date().toISOString() }, ...linked]);
      onToast(`${c.name}에 연결했습니다`);
    } catch (e) { onToast(errMsg(e, '연결하지 못했습니다'), 'error'); }
    finally { setBusy(false); }
  };
  const remove = async (l: LinkedCompany) => {
    const prev = linked;
    onLinked(linked.filter(x => x.company_id !== l.company_id));
    try { await unlinkCompany(itemId, l.company_id); }
    catch (e) { onLinked(prev); onToast(errMsg(e, '연결을 해제하지 못했습니다'), 'error'); }
  };

  const stLabel = st === 'saving' ? '저장 중…' : st === 'saved' ? '저장됨' : st === 'error' ? '저장하지 못했습니다' : '';
  return (
    <div onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      style={{ maxWidth: DETAIL_MAX, position: 'relative' }}>
      {drag && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', borderRadius: R.md, border: `2px dashed ${C.accent}`, background: 'rgba(239,246,255,.92)', boxSizing: 'border-box', padding: 12, textAlign: 'center' }}>
          <span style={{ fontSize: T.md, fontWeight: 700, color: C.accent }}>📎 여기에 놓으면 메모에 첨부됩니다</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <label htmlFor={`lw-memo-${itemId}`} style={labelStyle}>내 메모</label>
        <span aria-live="polite" style={{ fontSize: T.sm, color: st === 'error' ? C.red : st === 'saved' ? '#059669' : MUTED }}>{stLabel}</span>
      </div>
      <IMEArea id={`lw-memo-${itemId}`} value={text} onChange={setText} className="lw-f" placeholder="예: 창업감면 업종 판정 검토 시 인용"
        style={{ width: '100%', minHeight: 70, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '8px 10px', fontFamily: 'inherit', fontSize: T.sm, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
      <LegalMemoFiles st={fileSt} />
      {companies && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: T.sm, color: C.sub, whiteSpace: 'nowrap' }}>업체에 연결</span>
          <CompanyPicker companies={companies} exclude={linked.map(l => l.company_id)} disabled={busy} onPick={add} />
        </div>
      )}
      {companies && linked.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {linked.map(l => (
            <span key={l.company_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 10px', borderRadius: 14, background: C.accentLt, color: C.accent, fontSize: T.sm, fontWeight: 600 }}>
              🏢 {l.name}
              <button type="button" className="lw-f" onClick={() => remove(l)} aria-label={`${l.name} 연결 해제`} style={{ border: 0, background: 'transparent', color: C.accent, cursor: 'pointer', fontSize: T.sm, lineHeight: 1, padding: '0 4px' }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
